import { Box } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  getEnumLabel,
  getGenderOptions,
  getLanguageOptions,
} from "Forms/Utils/EnumOptions";
import { type ActorListItemDto, ActorStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { UserAvatar } from "Shared/Avatar/UserAvatar";
import { EntityStatusChip } from "Shared/Components/EntityStatusChip";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { Price } from "Shared/Price/Price";

export const ACTORS_SORTABLE_COLUMNS = [
  "name",
  "language",
  "age",
  "price",
] as const;

const isSortable = (id: string) =>
  (ACTORS_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useActorsTableColumns(): ColumnDef<ActorListItemDto>[] {
  const { t } = useResource();

  return useMemo(
    () => [
      {
        id: "name",
        header: t(Resources.Actors.Name),
        enableSorting: isSortable("name"),
        meta: { fillRow: true },
        cell: ({ row }) => {
          const nameParts = row.original.name.split(" ");
          const firstName = nameParts[0] ?? "";
          const lastName = nameParts.slice(1).join(" ");
          return (
            <NameLinkCell
              path={AppRouting.ActorDetail.path(row.original.actorID)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <UserAvatar
                  firstName={firstName}
                  lastName={lastName}
                  size={32}
                  imageUrl={row.original.profilePictureUrl}
                />
                <Box>{row.original.name}</Box>
              </Box>
            </NameLinkCell>
          );
        },
      },
      {
        id: "language",
        accessorKey: "language",
        header: t(Resources.Actors.NativeLanguage),
        enableSorting: isSortable("language"),
        cell: ({ getValue }) => (
          <Box>{getEnumLabel(getLanguageOptions(), getValue<string>())}</Box>
        ),
      },
      {
        accessorKey: "gender",
        header: t(Resources.Actors.Sex),
        enableSorting: isSortable("gender"),
        cell: ({ getValue }) => (
          <Box>{getEnumLabel(getGenderOptions(), getValue<string>())}</Box>
        ),
      },
      {
        id: "age",
        accessorKey: "age",
        header: t(Resources.Actors.Age),
        enableSorting: isSortable("age"),
        cell: ({ getValue }) => <Box>{getValue<number | null>() ?? "-"}</Box>,
      },
      {
        id: "price",
        accessorKey: "price",
        header: t(Resources.Actors.PricePerModule),
        enableSorting: isSortable("price"),
        cell: ({ getValue }) => {
          const price = getValue<number | null>();
          return (
            <Box>
              {price != null ? <Price amount={price} decimalPlaces={0} /> : "-"}
            </Box>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: t(Resources.Common.Status),
        enableSorting: false,
        cell: ({ getValue }) => (
          <EntityStatusChip
            isActive={getValue<ActorStatus>() === ActorStatus.Active}
            size="small"
          />
        ),
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: ({ row }) => (
          <DetailActionCell
            path={AppRouting.ActorDetail.path(row.original.actorID)}
          />
        ),
      },
    ],
    [t],
  );
}
